import { ExperienceModule } from "@intelligems/ig-types/dtos/experience-modules";
import { GetExperiencesType } from "@intelligems/ig-types/functions/experiences/get-experiences";
import { MODULE_ID_BY_EXPERIENCE_MODULE } from "@src/constants/billing";

export type ExperienceModuleFilter =
  | "Content Edits"
  | "Redirects"
  | "Styles & JavaScript"
  | "Theme Change"
  | "Template Changes"
  | "Amount off Products"
  | "Amount off Order"
  | "Volume Discount"
  | "Free Gift"
  | "Onsite Upsell"
  | "Buy X Get Y"
  | "Price Change"
  | "Shipping Rates"
  | "Free Shipping"
  | "Upsell"
  | "Trust Badge"
  | "Shipping Bar"
  | "Countdown Timer"
  | "Post-purchase Upsell";

export interface ExperienceModuleFilterGroup {
  label: string;
  options: ExperienceModuleFilter[];
}

export type ExperienceModuleTableConfigKey =
  | "all"
  | "content"
  | "merchandising"
  | "pricing"
  | "shipping"
  | "checkout"
  | "postPurchase";

export interface ExperienceModuleTableConfig {
  billingModuleGate?: {
    moduleId: string;
    requiredPermissionScopes: string[];
  };
  key: ExperienceModuleTableConfigKey;
  module?: ExperienceModule;
  experienceType?: GetExperiencesType;
  title: string;
  pageTitle: string;
  description?: string;
  modificationFilterOptions: ExperienceModuleFilter[];
  modificationFilterGroups?: ExperienceModuleFilterGroup[];
}

const CONTENT_MODIFICATION_FILTER_OPTIONS: ExperienceModuleFilter[] = [
  "Content Edits",
  "Redirects",
  "Styles & JavaScript",
  "Theme Change",
  "Template Changes",
];

const MERCHANDISING_MODIFICATION_FILTER_OPTIONS: ExperienceModuleFilter[] = [
  "Amount off Products",
  "Amount off Order",
  "Volume Discount",
  "Free Gift",
  "Onsite Upsell",
  "Buy X Get Y",
];

const PRICING_MODIFICATION_FILTER_OPTIONS: ExperienceModuleFilter[] = [
  "Price Change",
  "Amount off Products",
];

const SHIPPING_MODIFICATION_FILTER_OPTIONS: ExperienceModuleFilter[] = [
  "Shipping Rates",
  "Free Shipping",
];

const CHECKOUT_MODIFICATION_FILTER_OPTIONS: ExperienceModuleFilter[] = [
  "Upsell",
  "Trust Badge",
  "Countdown Timer",
  "Shipping Bar",
];

const POST_PURCHASE_MODIFICATION_FILTER_OPTIONS: ExperienceModuleFilter[] = [
  "Post-purchase Upsell",
];

export const EXPERIENCE_MODULE_TABLE_CONFIGS = {
  all: {
    key: "all",
    title: "All Experiences",
    pageTitle: "Welcome!",
    description: "",
    modificationFilterOptions: [],
    modificationFilterGroups: [
      { label: "Content", options: CONTENT_MODIFICATION_FILTER_OPTIONS },
      {
        label: "Merchandising",
        options: MERCHANDISING_MODIFICATION_FILTER_OPTIONS,
      },
      { label: "Pricing", options: PRICING_MODIFICATION_FILTER_OPTIONS },
      { label: "Shipping", options: SHIPPING_MODIFICATION_FILTER_OPTIONS },
      { label: "Checkout", options: CHECKOUT_MODIFICATION_FILTER_OPTIONS },
      {
        label: "Post-purchase",
        options: POST_PURCHASE_MODIFICATION_FILTER_OPTIONS,
      },
    ],
  },
  content: {
    billingModuleGate: {
      moduleId: MODULE_ID_BY_EXPERIENCE_MODULE.content,
      requiredPermissionScopes: ["start_content_experiment"],
    },
    key: "content",
    module: "content",
    experienceType: "personalization",
    title: "Content Experiences",
    pageTitle: "Content",
    description:
      "Experiences that change what shoppers see, where, and for whom.",
    modificationFilterOptions: CONTENT_MODIFICATION_FILTER_OPTIONS,
  },
  merchandising: {
    billingModuleGate: {
      moduleId: MODULE_ID_BY_EXPERIENCE_MODULE.merchandising,
      requiredPermissionScopes: ["start_discount_experiment", "start_offer"],
    },
    key: "merchandising",
    module: "merchandising",
    experienceType: "offerPersonalization",
    title: "Merchandising Experiences",
    pageTitle: "Merchandising",
    description:
      "Lift cart value with offers, gifts, and bundles before checkout.",
    modificationFilterOptions: MERCHANDISING_MODIFICATION_FILTER_OPTIONS,
  },
  pricing: {
    billingModuleGate: {
      moduleId: MODULE_ID_BY_EXPERIENCE_MODULE.pricing,
      requiredPermissionScopes: ["start_price_experiment"],
    },
    key: "pricing",
    module: "pricing",
    title: "Pricing Experiences",
    pageTitle: "Pricing",
    description:
      "Real price tests and personalized pricing for selected products.",
    modificationFilterOptions: PRICING_MODIFICATION_FILTER_OPTIONS,
  },
  shipping: {
    billingModuleGate: {
      moduleId: MODULE_ID_BY_EXPERIENCE_MODULE.shipping,
      requiredPermissionScopes: ["start_shipping_experiment"],
    },
    key: "shipping",
    module: "shipping",
    title: "Shipping Experiences",
    pageTitle: "Shipping",
    description:
      "Tune rates and free-shipping thresholds to balance margin and conversion.",
    modificationFilterOptions: SHIPPING_MODIFICATION_FILTER_OPTIONS,
  },
  checkout: {
    billingModuleGate: {
      moduleId: MODULE_ID_BY_EXPERIENCE_MODULE.checkout,
      requiredPermissionScopes: [
        "start_checkout_block_test",
        "start_checkout_block_experience",
      ],
    },
    key: "checkout",
    module: "checkout",
    experienceType: "checkoutPersonalization",
    title: "Checkout Experiences",
    pageTitle: "Checkout",
    description: "Upsells, trust, and content that convert more at checkout.",
    modificationFilterOptions: CHECKOUT_MODIFICATION_FILTER_OPTIONS,
  },
  postPurchase: {
    billingModuleGate: {
      moduleId: MODULE_ID_BY_EXPERIENCE_MODULE.postPurchase,
      requiredPermissionScopes: [
        "start_checkout_block_test",
        "start_checkout_block_experience",
      ],
    },
    key: "postPurchase",
    module: "postPurchase",
    experienceType: "postPurchasePersonalization",
    title: "Post-purchase",
    pageTitle: "Post-purchase",
    description: "One-click upsells after the buy button.",
    modificationFilterOptions: [],
  },
} satisfies Record<string, ExperienceModuleTableConfig>;

export const EXPERIENCE_CATEGORY_TABS = [
  { label: "All Experiences", value: "all" },
  { label: "Tests", value: "tests" },
  { label: "Personalizations", value: "personalizations" },
] as const;

export type ExperienceCategoryTab =
  (typeof EXPERIENCE_CATEGORY_TABS)[number]["value"];

export const MODULE_STATUS_FILTER_OPTIONS = [
  "Pending",
  "Active",
  "Paused",
  "Ended",
  "Scheduled",
  "Archived",
] as const;

export type ModuleStatusFilter = (typeof MODULE_STATUS_FILTER_OPTIONS)[number];
